import { Signature } from "@/type/core/dashboard/dashboard.types";
import { Picture } from "@/type/core/offices/offices.types";
import { SignaturePicturePriorityEnum } from "@/utils/enums/signature.picture.priority.enum";
import { CheckCircleFilled } from "@ant-design/icons";
import { FunctionComponent, useState } from "react";
import { useTranslation } from "react-i18next";
import CheckedLabel from "@/components/core/labels/CheckedLabel";
import BasicModal, {
  BasicModalProps,
} from "@components/core/modals/BasicModal";
import imageECMA from "@static/core/img/logo_ecma.png";
import i18n from "i18next";

interface ChooseSealingImageModalProps extends BasicModalProps {
  userSealingPictures: Picture[];
  officeSealingPictures: Picture[];
  defaultSelectedPicture?: Picture;
  defaultSealingPriorityRule?: SignaturePicturePriorityEnum;
  onSubmitNewPicture: (
    sealing: Pick<Signature, "signaturePictureRank" | "signaturePriorityRule">,
  ) => void;
}

const ChooseSealingImageModal: FunctionComponent<
  ChooseSealingImageModalProps
> = ({
  officeSealingPictures,
  defaultSelectedPicture,
  defaultSealingPriorityRule,
  onSubmitNewPicture,
  ...props
}) => {
  const { t } = useTranslation();

  const [selectedPicture] = useState<Picture | undefined>(
    defaultSelectedPicture,
  );
  const [signaturePriorityRule, setSignaturePriorityRule] = useState<
    SignaturePicturePriorityEnum | undefined
  >(defaultSealingPriorityRule);

  const selectableSealing = (
    picture: Picture,
    key: string,
    priorityRule: SignaturePicturePriorityEnum,
  ) => {
    const isSelected =
      (selectedPicture?.rank === undefined ||
        picture.rank == selectedPicture?.rank) &&
      priorityRule == signaturePriorityRule;
    return (
      <div
        key={key}
        className={`signature ${isSelected ? "selected" : "selectable"}`}
        style={{ display: "grid", cursor: "default" }}
      >
        <img className="signature-image" src={picture.picture} />
        {isSelected && <CheckCircleFilled className="check-sign" />}
      </div>
    );
  };

  const ecmaPicture: Picture = {
    picture: imageECMA,
    rank: 1,
  };

  return (
    <BasicModal
      {...props}
      onOk={() => {
        onSubmitNewPicture({
          signaturePictureRank: selectedPicture?.rank,
          signaturePriorityRule,
        });
        props.onOk?.();
      }}
    >
      <div className="p-20">
        <div className="grid-container">
          {officeSealingPictures.map((officeSignaturePicture, index) => {
            return (
              officeSignaturePicture.picture &&
              selectableSealing(
                officeSignaturePicture,
                `ChooseSignatureImageModal_officeSignaturePicture${index}`,
                SignaturePicturePriorityEnum.SCELLEMENT_PICTURE,
              )
            );
          })}
          {officeSealingPictures.length === 0 &&
            selectableSealing(
              ecmaPicture,
              `ChooseSignatureImageModal_officeSignaturePicture${1}`,
              SignaturePicturePriorityEnum.SCELLEMENT_PICTURE,
            )}
          <div className="col-6">
            <a
              className="text-primary scellement"
              onClick={() => {
                setSignaturePriorityRule(
                  SignaturePicturePriorityEnum.SCELLEMENT_PICTURE,
                );
              }}
            >
              {i18n.t(
                "signature.chooseImageModal.makeDefault.scellementPicture",
              )}
            </a>
          </div>
        </div>
        <div className="grid-container">
          <div className="no-picture-container" style={{ cursor: "default" }}>
            <CheckedLabel
              text={t("modals.info.NoScellementPicture.title")}
              isChecked={
                !selectedPicture &&
                signaturePriorityRule ===
                  SignaturePicturePriorityEnum.NO_PICTURE
              }
            />
          </div>
          <a
            className="text-primary scellement"
            onClick={() => {
              setSignaturePriorityRule(SignaturePicturePriorityEnum.NO_PICTURE);
            }}
          >
            {i18n.t(
              "signature.chooseImageModal.makeDefault.noScellementPicture",
            )}
          </a>
        </div>
      </div>
    </BasicModal>
  );
};

export default ChooseSealingImageModal;
